import { RingBuffer } from "ring-buffer-ts";

import {
  Manifest,
  ManifestRecord,
  TrackingData,
  TrackingFrame,
} from "mannequin/src/types/tracking/mannequin";
import { SkeletalApi } from "../../api/SkeletalApi";
import { Context, PoserMode } from "../context";
import { PoserClock } from "../util/clock";
import {
  ManifestMetadata,
  ManifestPlaysMap,
} from "mannequin/src/types/tracking/mannequin/manifest";
import { TrackLabels } from "mannequin/src/transform/track-consolidator/track-consolidator";
import { TrackingDataSerializer } from "mannequin/src/types/tracking/mannequin/track/data";
import {
  GameEvent,
  isGameEventKeyFrameComplete,
} from "mannequin/src/types/tracking/mannequin/game-event";
import { TrackingDataWire } from "mannequin/src/types/flatbuffers/mannequin";
import { UniformVariants } from "../../lib/skin/material/variant";

interface Record extends ManifestRecord {
  fetchState: FetchState;
}

interface AnnotatedManifest extends Manifest {
  records: Record[];
}

type ManifestLimits = { start: number; live: number };

enum FetchState {
  NONE = "none",
  FETCH_PENDING = "pending",
  FETCH_FAILED = "failed",
  FETCH_SUCCESS = "success",
}

export class BufferManager {
  frames: RingBuffer<TrackingFrame>;
  manifest: AnnotatedManifest = {
    version: "unknown",
    records: [],
    startTime: Infinity,
    endTime: -Infinity,
  };
  // TODO: move to another class
  metadata?: ManifestMetadata;
  labels?: TrackLabels;
  plays?: ManifestPlaysMap;
  uniforms?: UniformVariants;

  // allow for one second missing
  marginSeconds = 1;
  currRelativeIndex?: number;
  currIndex?: number;

  private contextRef?: Context;

  private skeletalApi?: SkeletalApi;

  private initialRecordIndex?: number;
  private recordsFetched: Set<Record> = new Set();

  private pendingChunks: Map<number, TrackingData | null> = new Map();

  // NB: this is very conservative and will only catch big errors in manifest,
  //     but since we don't have proper discontinuity handling in the chunker,
  //     I think this has to be min 5 s
  // TODO: check above assertion
  private consistencyTolerance = 5; //s
  private maxPendingChunks = 3;

  private bufferLengthSeconds?: number;

  private indexBufferConsideredLive = 2;

  private fetchIntervalId = -1;

  constructor(capacity = 1500) {
    this.frames = new RingBuffer<TrackingFrame>(capacity);
  }

  async init(context: Context): Promise<void> {
    const options = context.options;
    this.skeletalApi = await SkeletalApi.make(options);
    if (!this.skeletalApi) {
      console.error("no api link established");
      return;
    }

    this.uniforms = await UniformVariants.make(context, this.skeletalApi);

    this.bufferLengthSeconds = this.frames.getSize() / context.trackingFps;

    this.contextRef = context;

    // TODO: update based on chunk period
    this.fetchIntervalId = self.setInterval(() => this.poll(), 5000);

    return this.poll();
  }

  teardown() {
    window.clearInterval(this.fetchIntervalId);
  }

  setBearerToken(token: string) {
    if (!this.skeletalApi) {
      console.error("can't set bearer token on uninitialized buffer manager");
      return;
    }
    this.skeletalApi.setBearerToken(token);
  }

  getLimits(): ManifestLimits | undefined {
    if (this.manifest.records.length == 0) {
      return undefined;
    }

    const now = new Date().getTime() / 1000;
    const currIndex = this.findChunkIndex(now) ?? Infinity;

    const maxIndex =
      this.manifest.records.length - 1 - this.indexBufferConsideredLive;

    return {
      live: this.manifest.records[Math.min(maxIndex, currIndex)].startTime,
      start: this.manifest.records[0].startTime,
    };
  }

  update(clock: PoserClock) {
    if (
      this.bufferLengthSeconds === undefined ||
      !this.skeletalApi ||
      !this.contextRef
    ) {
      console.error("can't update buffer without initializing");
      return;
    }

    // if we've seeked outside of current buffer, find correct manifest record
    this.currRelativeIndex = this.findChunkIndex(clock.real);
    if (this.currRelativeIndex == undefined) {
      // only error if we're failing to update after clock is initialized
      // TODO: debounce this error?
      if (clock.real != 0)
        console.error(
          `can't find manifest index for ${clock.real} (${clock.relative})`
        );
      return;
    }

    this.currIndex = this.manifest.records[this.currRelativeIndex]?.index;

    // check if we need to blank slate the buffer state
    if (
      this.initialRecordIndex != this.currRelativeIndex &&
      (clock.real <= clock.bufferLimits[0] ||
        clock.real > clock.bufferLimits[1])
    ) {
      this.resetTrackingData(this.currRelativeIndex);
    } else if (this.currRelativeIndex != this.initialRecordIndex) {
      this.initialRecordIndex = undefined;
    }

    // find chunks to fetch
    const records = this.manifest.records;
    let pendingCnt = 0;
    for (
      let i = this.currRelativeIndex;
      i < records.length && pendingCnt < this.maxPendingChunks;
      i++
    ) {
      // look into chunk state - only fetch next one when last one is in buffer
      const record = records[i];

      let maxTime = clock.real + this.bufferLengthSeconds;
      // if we're in PLAY mode, then max time is the end of the play, plus a chunk
      if (clock.playLimits) {
        // accounts for plays > this.bufferLengthSeconds
        maxTime = Math.min(
          clock.playLimits[1] + (record.duration ?? 5),
          maxTime
        );
      }
      if (
        record.duration != undefined &&
        record.startTime + record.duration > maxTime
      ) {
        // fetching record will take buffer start beyond t
        // OR will result in buffering on loop for PLAY mode
        break;
      }

      const blankSlate = i === this.initialRecordIndex;
      const state = record.fetchState;

      const onFail = (reason: any) => {
        console.error(
          `failed fetching chunk for record ${i} records.length: ${records.length}`,
          reason
        );
        record.fetchState = FetchState.FETCH_FAILED;
      };

      switch (state) {
        case FetchState.FETCH_FAILED:
        case FetchState.NONE:
          record.fetchState = FetchState.FETCH_PENDING;
          // flag chunk as pending
          this.pendingChunks.set(record.index, null);

          this.skeletalApi
            .fetchChunk(record.index)
            .then(
              (data) => {
                // fetch cancelled because of seek
                if (record.fetchState != FetchState.FETCH_PENDING) {
                  // TODO: remove debug
                  this.pendingChunks.delete(record.index);
                  console.error(`fetch of record ${record.index} cancelled`);
                  return;
                }
                const chunk =
                  data instanceof TrackingDataWire
                    ? TrackingDataSerializer.deserializeBinary(data)
                    : TrackingDataSerializer.deserializeLegacy(data);

                if (chunk && this.checkConsistency(chunk, record)) {
                  record.fetchState = FetchState.FETCH_SUCCESS;
                  this.recordsFetched.add(record);

                  if (blankSlate && chunk.eventKeyFrame !== undefined) {
                    // add keyframe game events to first frame in buffer
                    if (!chunk.frames[0].gameEvents) {
                      chunk.frames[0].gameEvents = [];
                    }

                    // warn on incomplete keyFrame
                    if (!isGameEventKeyFrameComplete(chunk.eventKeyFrame)) {
                      console.warn(
                        "incomplete event keyFrame:",
                        chunk.eventKeyFrame
                      );
                    }
                    const events: GameEvent[] = [];
                    for (const keyFrameEvents of Object.values(
                      chunk.eventKeyFrame
                    )) {
                      if (Array.isArray(keyFrameEvents)) {
                        events.push(...keyFrameEvents);
                      } else {
                        events.push(keyFrameEvents);
                      }
                    }

                    // label that event was in keyframe (for debug)
                    events.forEach((e) => (e.isKeyFramed = true));
                    // insert key frame events and sort in client (just in case)
                    chunk.frames[0].gameEvents.unshift(...events);
                    // TODO: determine if we need this sort
                    chunk.frames[0].gameEvents.sort((a, b) => a.time - b.time);
                  }

                  const existing = this.pendingChunks.get(record.index);
                  if (existing != null) {
                    console.error(
                      `trying to add record ${i} to intermediate buffer multiple times`
                    );
                  } else {
                    this.pendingChunks.set(record.index, chunk);
                  }
                } else {
                  console.error(`fetched undefined chunk ${record.index}`);
                }
              },
              (reason) => onFail(reason)
            )
            .catch((reason) => onFail(reason));

        // fallthrough
        case FetchState.FETCH_PENDING:
          pendingCnt++;
          break;

        default:
          break;
      }
    }

    // add pending chunks to buffer
    // relies on map being sorted in order of insertion
    let add = false;
    for (const index of this.pendingChunks.keys()) {
      const chunk = this.pendingChunks.get(index);
      if (chunk === undefined) {
        console.error(`blank chunk at ${index} found`);
        break;
      }

      if (chunk === null) {
        // waiting for fetch, so ignore
        // console.log(`blank chunk`);
        break;
      }

      // check to see if new buffer is contiguous with ring buffer
      if (clock.bufferPeriod == 0) {
        console.log(`starting buffer with chunk ${index}`);

        this.frames.clear();
        add = true;
      } else if (chunk.limits[0] > clock.bufferLimits[1]) {
        if (chunk.limits[0] <= clock.bufferLimits[1] + this.marginSeconds) {
          // console.log(`chunk ${index} contiguous, appending`);
          // add frames to ring buffer
          add = true;
        } else {
          // jumped forward - discontinuity
          console.warn(
            `chunk ${index} jumped forward - discontinuity ` +
              `(${chunk.limits[0]} >> ${clock.bufferLimits[1]}), ` +
              `marking frames < ${clock.real} for deletion`
          );
          for (
            let frame = this.frames.getFirst();
            frame && frame.time < clock.real;
            frame = this.frames.removeFirst()
          ) {}
          add = true;
        }
      } else {
        if (chunk.limits[0] > clock.bufferLimits[0]) {
          console.error(`fetched chunk ${index} already in buffer`);
        } else {
          console.warn(
            `chunk ${index} jumped backward - discontinuity ` +
              `(${chunk.limits[0]} < ${clock.bufferLimits[0]})`
          );
          // jumped backward - discontinuity
          this.frames.clear();
          add = true;
        }
      }

      this.pendingChunks.delete(index);

      if (add) {
        // for (const frame of chunk.frames) {
        //   console.log(`fn: ${frame.num}, ${frame.time}`);
        // }
        this.frames.add(...chunk.frames);
        this.contextRef.clock.update(this);
      } else {
        break;
      }
    }

    if (add) {
      this.contextRef.ui?.setTimelinePeriod(clock.timelinePeriod);
    }

    // look through records we know about and check if they're still in buffer
    for (const record of this.recordsFetched) {
      const duration = record.duration ?? 0;
      if (record.startTime + duration < clock.bufferLimits[0]) {
        record.fetchState = FetchState.NONE;
        this.recordsFetched.delete(record);
      }
    }
  }

  private async poll() {
    const promises: Promise<void>[] = [];
    promises.push(this.fetchMetadata());
    promises.push(this.fetchTrackLabels());
    promises.push(this.fetchManifest());
    if (this.contextRef?.options.playbackMode === PoserMode.PLAY) {
      promises.push(this.fetchPlays());
    }
    await Promise.all(promises);
  }

  private async fetchMetadata(): Promise<void> {
    this.metadata = await this.skeletalApi?.fetchMetadata();
  }

  private async fetchTrackLabels(): Promise<void> {
    this.labels = await this.skeletalApi?.fetchTrackLabels();
  }

  private async fetchPlays(): Promise<void> {
    this.plays = await this.skeletalApi?.fetchPlays();
  }

  private async fetchManifest(): Promise<void> {
    if (!this.skeletalApi) {
      return Promise.reject("buffer manager is uninitialized");
    }
    const rawManifest = await this.skeletalApi.fetchManifest();

    const numExisting = this.manifest.records.length;
    const records = this.manifest.records;

    if (rawManifest === undefined) {
      console.error(`no manifest returned for ${this.skeletalApi.gamePk}`);
      return;
    }

    // copy in metadata
    this.manifest.startTime = rawManifest.startTime;
    this.manifest.endTime = rawManifest.endTime;
    this.manifest.version = rawManifest.version;

    // update records
    // TODO: check for inconsistencies with cached manifest here and warn / update
    for (let i = numExisting; i < rawManifest.records.length; i++) {
      const raw = rawManifest.records[i];
      const curr: Record = {
        startTime: raw.startTime,
        fetchState: FetchState.NONE,
        index: raw.index,
        duration: raw.duration,
      };

      records.push(curr);
    }
  }

  private findChunkIndex(realSeconds: number): number | undefined {
    const records = this.manifest.records;
    if (records.length == 0) {
      return undefined;
    } else {
      // TODO: use binary search here instead
      for (let i = 0; i < records.length; i++) {
        const curr = records[i];
        const endTime =
          curr.duration != undefined
            ? curr.startTime + curr.duration
            : Infinity;
        if (realSeconds >= curr.startTime && realSeconds < endTime) {
          return i;
        }
      }
    }
    return undefined;
  }

  private resetTrackingData(currIndex: number) {
    for (const record of this.manifest.records) {
      record.fetchState = FetchState.NONE;
    }
    this.recordsFetched.clear();
    this.pendingChunks.clear();
    this.initialRecordIndex = currIndex;
  }

  private checkConsistency(chunk: TrackingData, record: Record): boolean {
    const chunkStartS = chunk.limits[0];
    const chunkDurationS = chunk.limits[1] - chunk.limits[0];

    const startDiff = Math.abs(chunkStartS - record.startTime);
    const durationDiff =
      record.duration != undefined
        ? Math.abs(chunkDurationS - record.duration)
        : 0;

    const consistent =
      startDiff < this.consistencyTolerance &&
      durationDiff < this.consistencyTolerance;
    if (!consistent) {
      console.error(
        `chunk and record not aligned for index ${record.index}, deltas - start: ${startDiff} duration: ${durationDiff}`
      );
    }

    return consistent;
  }
}
